import clsx from 'clsx'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { ProductCarouselContext } from './context'

function findNextSibling (element: Element, selector: string) {
  let sibling = element.nextElementSibling

  while (sibling) {
    if (sibling.matches(selector)) return sibling
    sibling = sibling.nextElementSibling
  }

  return null
}

function findPreviousSibling (element: Element, selector: string) {
  let sibling = element.previousElementSibling

  while (sibling) {
    if (sibling.matches(selector)) return sibling
    sibling = sibling.previousElementSibling
  }

  return null
}

function getMaskStyle (maskLeft: boolean, maskRight: boolean) {
  const fadeWidth = '2rem'
  const createMask = (value: string) => ({
    maskImage: value,
    WebkitMaskImage: value,
  })

  if (maskLeft && maskRight) {
    return createMask(
      `linear-gradient(to right, transparent, black ${fadeWidth}, black calc(100% - ${fadeWidth}), transparent)`
    )
  } else if (maskLeft) {
    return createMask(
      `linear-gradient(to right, transparent, black ${fadeWidth})`
    )
  } else if (maskRight) {
    return createMask(
      `linear-gradient(to right, black calc(100% - ${fadeWidth}), transparent)`
    )
  } else {
    return createMask('none')
  }
};

function NavigationButton ({
  onClick,
  show,
  direction,
}: {
  onClick: () => void;
  show: boolean;
  direction: 'previous' | 'next';
}) {
  return (
    <button
      // This button is only useful for sighted users. Screen readers can read
      // the whole tablist regardless of the scroll overflow.
      aria-hidden
      className={clsx(
        'absolute top-0 h-full opacity-1 transition flex items-center text-gray-500 hover:text-gray-800 outline-none group/navigation-button',
        {
          '-left-4': direction === 'previous',
          '-right-4': direction === 'next',
          'opacity-0 pointer-events-none': !show,
        }
      )}
      disabled={!show}
      onClick={onClick}
      tabIndex={show ? 0 : -1}
    >
      <div className='w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center outline outline-2 outline-offset-2 outline-transparent group-focus-visible/navigation-button:outline-thatch-500'>
        {direction === 'previous' && <ChevronLeftIcon size={24} />}
        {direction === 'next' && <ChevronRightIcon size={24} />}
      </div>
    </button>
  )
}

export function ProductCarouselList () {
  const [showPreviousButton, setShowPreviousButton] = useState(false)
  const [showNextButton, setShowNextButton] = useState(false)
  const { activeItem, portalId } = useContext(ProductCarouselContext)

  const listRef = useRef<HTMLDivElement>(null)
  const startMarkerRef = useRef<HTMLDivElement>(null)
  const endMarkerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const list = listRef.current
    const item = list?.querySelector('[data-carousel-tab][aria-selected=true]')

    if (list && item) {
      const itemRect = item.getBoundingClientRect()
      const listRect = list.getBoundingClientRect()

      if (itemRect.left < listRect.left || itemRect.right > listRect.right) {
        item.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }
    }
  }, [activeItem])

  useEffect(() => {
    const list = listRef.current

    function selectNext () {
      const item = document.getElementById(activeItem || '')

      if (item) {
        const nextItem = findNextSibling(item, '[data-carousel-item]')

        nextItem?.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
          inline: 'nearest'
        })
      }
    }

    function selectPrevious () {
      const item = document.getElementById(activeItem || '')

      if (item) {
        const previousItem = findPreviousSibling(item, '[data-carousel-item]')

        previousItem?.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
          inline: 'nearest',
        })
      }
    }

    function handleKeydown (event: KeyboardEvent) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          selectPrevious()
          break
        case 'ArrowRight':
          event.preventDefault()
          selectNext()
          break
      }
    }

    if (list) {
      /**
       * The tabs are portaled into this div so React's virtual events won't
       * bubble up to it. Instead we need to add an normal event listener.
       **/
      list.addEventListener('keydown', handleKeydown)

      return () => {
        list.removeEventListener('keydown', handleKeydown)
      }
    }
  }, [activeItem])

  useLayoutEffect(() => {
    const list = listRef.current
    const startMarker = startMarkerRef.current
    const endMarker = endMarkerRef.current

    if (list && startMarker && endMarker) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target === startMarker) {
              setShowPreviousButton(!entry.isIntersecting)
            } else if (entry.target === endMarker) {
              setShowNextButton(!entry.isIntersecting)
            }
          })
        },
        {
          root: list,
          threshold: [0, 1.0],
        }
      )

      observer.observe(startMarker)
      observer.observe(endMarker)

      return () => {
        observer.disconnect()
      }
    }
  }, [setShowPreviousButton, setShowNextButton])

  return (
    <div className='relative h-16 rounded-lg flex outline outline-2 outline-offset-4 outline-transparent has-[.carousel-list-wrapper:focus-visible]:outline-thatch-500'>
      <div
        className='h-full overflow-x-auto carousel-list-wrapper outline-none'
        ref={listRef}
        role='tablist'
        style={getMaskStyle(showPreviousButton, showNextButton)}
        tabIndex={0}
      >
        <div className='relative h-full w-max'>
          <div
            className='w-px absolute left-0 top-0 h-full pointer-events-none'
            ref={startMarkerRef}
          />
          <div className='flex gap-2 h-full w-max' id={portalId} />
          <div
            className='w-px absolute right-0 top-0 h-full pointer-events-none'
            ref={endMarkerRef}
          />
        </div>
      </div>
      <NavigationButton
        onClick={() => {
          const list = listRef.current
          list?.scrollBy({ left: -list.clientWidth, behavior: 'smooth' })
        }}
        show={showPreviousButton}
        direction='previous'
      />
      <NavigationButton
        onClick={() => {
          const list = listRef.current
          list?.scrollBy({ left: list.clientWidth, behavior: 'smooth' })
        }}
        show={showNextButton}
        direction='next'
      />
    </div>
  )
}
